import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useFetch } from '@/modules/shared/composables/use-fetch'

// AKA "Group", but group is reserved in Ruby
type Workspace = {
  id: string | number
  filing_folder_root_id: string
  name: string
  subdomain: string
  logo_url: string | null
  //
  options: {
    /** Discussions-tab visibility on company profiles — member-readable,
     *  unlike the admin-gated workspace settings payload (tickets/2658). */
    company_discussions_enabled: boolean
    /** Deal-pipeline investor access (tickets/3128) — the sidenav link
     *  shows only where DealPolicy#index? would admit the investor. */
    deal_pipeline_enabled: boolean
    is_investment_group: boolean
    enable_investor_aml_verification: boolean
    is_accreditation_required: boolean
    is_aml_enabled: boolean
    is_cc_recipients_enabled: boolean
    is_company_list_enabled: boolean
    /** Featured-deal surfaces SPA-wide (tickets/3122) — off by default;
     *  every featured affordance hides unless this ships true. */
    is_featured_deals_enabled: boolean
    is_k1_self_serve_uploads_enabled: boolean
    is_registered_investment_advisor: boolean
    is_setup_mode_enabled: boolean
    hide_investors_from_investors: boolean
    show_chapters_to_investors: boolean
    can_access_rails: boolean
    has_connections_access: boolean
  }

  is_candidate_complete: boolean
  has_connections: boolean
  connecting_candidate_id: string
  accreditation_level_min: string | null

  //
  entity_count: number
  has_entities_with_taxes: boolean
  individual_count: number
  fund_count: number
  gp_count: number
  spv_count: number
}

const initialState: Workspace = {
  id: null,
  filing_folder_root: null,
  name: null,
  subdomain: null,
  logo_url: null,
  options: {
    company_discussions_enabled: false,
    deal_pipeline_enabled: false,
    is_investment_group: false,
    enable_investor_aml_verification: false,
    is_accreditation_required: false,
    is_aml_enabled: false,
    is_cc_recipients_enabled: false,
    is_company_list_enabled: false,
    is_featured_deals_enabled: false,
    is_k1_self_serve_uploads_enabled: false,
    is_registered_investment_advisor: false,
    is_setup_mode_enabled: false,
    hide_investors_from_investors: true,
    show_chapters_to_investors: false,
    can_access_rails: false,
    has_connections_access: false,
  },
  is_candidate_complete: false,
  has_connections: false,
  connecting_candidate_id: null,
  accreditation_level_min: null,
  entity_count: 0,
  has_entities_with_taxes: false,
  individual_count: 0,
  fund_count: 0,
  gp_count: 0,
  spv_count: 0,
}

export const useWorkspaceStore = defineStore('workspaceStore', () => {
  const route = useRoute()
  const baseUrl = computed(() => `/${route.params.slug}`)
  const current = ref({ ...initialState })

  const fetchCurrent = async (slug?: string) => {
    const url = slug ? `/${slug}` : baseUrl.value
    const { data } = await useFetch(`${url}/workspace`).get().json<{ data: Workspace }>()
    current.value = data.value.data
  }

  const reset = () => {
    current.value = { ...initialState }
  }

  return {
    current,
    fetchCurrent,
    reset,
  }
})
